import { AxiosResponse } from 'axios';
import { loadingSignal } from 'components/Loading';
import apis from 'services/apis';
import { IAptListResPayload } from 'services/types/response';
import store from 'store';
import turnApis from 'Turn/services/apis';
import { MASTER_ADMIN_PIN_CODE } from 'Turn/services/constants';
import { IPinCodeResData } from 'Turn/services/types/staff';
import moment from 'utils/moment';
import storage from 'utils/sessionStorage';
import { verifyPinCode } from 'widgets/ModalVerifyAdminPin';
import { sameDayBookingAlertRef } from '.';
import { toSameDayRows } from './same-day-appointments';

/** Staff who confirmed the booking. Sent with the booking payload so the server can record it. */
export type IBookingActor = { pinCode: string; actorStaffId: number };

/**
 * - `proceed`: book as usual; `actor` is set only when a staff confirmed a same-day booking.
 * - `cancel`: the user backed out — do not book.
 */
export type ISameDayBookingResult = { status: 'proceed'; actor?: IBookingActor } | { status: 'cancel' };

const PROCEED: ISameDayBookingResult = { status: 'proceed' };

type Params = {
  customerId?: number | string | null;
  /** Booking start, `MM/DD/YYYY HH:mm` — the format every booking payload already carries. */
  startTime: string;
  /** Group bookings are not checked. */
  totalPeople?: number;
};

const fetchCustomerAppointmentsOfDay = async (customerId: number, date: string) => {
  const res: AxiosResponse<{ payload: IAptListResPayload }> = await apis.getAppointmentLists(storage.shopId.get(), {
    customerId: customerId.toString(),
    start_time: date,
    end_time: date,
    page: 1,
  });
  return res?.data?.payload?.data || [];
};

/**
 * Asks for a real staff PIN (the master PIN is refused) and resolves with who entered it.
 * Closing the PIN prompt leaves the promise pending, exactly like the other PIN gates:
 * the booking simply does not happen.
 */
const askStaffPin = () => new Promise<IBookingActor>(resolve => {
  verifyPinCode.current?.open(async (pinCode) => {
    if (pinCode === MASTER_ADMIN_PIN_CODE) return false;
    try {
      const res: AxiosResponse<{ payload: IPinCodeResData | null }> = await turnApis.checkPinCodeAdminV2(pinCode);
      const staff = res?.data?.payload;
      if (!staff?.staffId) return false;
      resolve({ pinCode, actorStaffId: staff.staffId });
      return true;
    } catch (error) {
      return false;
    }
  }, { title: 'Enter your PIN to book' });
});

/**
 * Before creating a booking: if the customer already has a NEW/CONFIRMED appointment on that
 * day, warn and require a staff PIN to continue. Only for allowlisted shops; walk-ins and group
 * bookings skip the check. A failed lookup never blocks booking.
 */
export const confirmSameDayBooking = async ({ customerId, startTime, totalPeople = 1 }: Params): Promise<ISameDayBookingResult> => {
  if (!store.getState().shop.setting.sameDayBookingCheck) return PROCEED;
  const id = Number(customerId);
  if (!id || totalPeople >= 2) return PROCEED;
  const date = moment(startTime, 'MM/DD/YYYY HH:mm');
  if (!date.isValid()) return PROCEED;

  loadingSignal.value = true;
  let list;
  try {
    list = await fetchCustomerAppointmentsOfDay(id, date.format('YYYY-MM-DD'));
  } catch (error) {
    return PROCEED;
  } finally {
    loadingSignal.value = false;
  }

  const rows = toSameDayRows(list);
  if (!rows.length) return PROCEED;

  const ok = await sameDayBookingAlertRef.current?.confirm(rows, list[0]?.customerName);
  if (!ok) return { status: 'cancel' };
  const actor = await askStaffPin();
  return { status: 'proceed', actor };
};
